import { BaseControl, BaseControlProps, Checkbox, Input, useTranslations } from "@harea/component";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useEmployeeListQuery } from "../hooks/useEmployeeListQuery";

const EmployeeListControl = (props: Props) => {

    const [PLACEHOLDER_SEARCH, NO_SEARCH_RESULT, SELECT_ALL] = useTranslations(i18nKeys);

    const { data: contracts } = useEmployeeListQuery();
    const [searchTerm, setSearchTerm] = useState('');

    const allMembers = useMemo(() => {
        return contracts?.data
            ?.filter(contract => !!contract?.user?.id)
            .map(contract => ({
                id: contract.user.id!,
                label: `${contract.user.firstName ?? ''} ${contract.user.lastName ?? ''}`.trim(),
                value: contract.user.id!,
            })) || [];
    }, [contracts]);

    const filteredMembers = useMemo(() => {
        if (!searchTerm.trim()) return allMembers;
        const searchLower = searchTerm.toLowerCase();
        return allMembers.filter(member =>
            member.label.toLowerCase().includes(searchLower)
        );
    }, [allMembers, searchTerm]);

    const checkboxValue = useMemo(() => {
        const value = props.value || [];
        return value.filter(id => id !== undefined && id !== null);
    }, [props.value]);

    const handleChange = useCallback((value: any) => {
        props.onChange?.(value);
    }, [props.onChange]);

    const handleSelectAll = useCallback(() => {
        const allMemberIds = filteredMembers.map(member => member.id);
        props.onChange?.(allMemberIds);
    }, [filteredMembers, props.onChange]);

    const handleDeselectAll = useCallback(() => {
        props.onChange?.([]);
    }, [props.onChange]);

    const isAllSelected = useMemo(() => {
        if (filteredMembers.length === 0) return false;
        return filteredMembers.every(member => checkboxValue.includes(member.id));
    }, [filteredMembers, checkboxValue]);

    useEffect(() => {
        if (!props.value || props.value.length === 0) {
            if (contracts && contracts.data.length > 0) {
                const groupMemberIds = contracts.data.map(contract => contract.user?.id!);
                props.onChange?.(groupMemberIds);
            }
        }
    }, [contracts]);

    return (
        <BaseControl {...props}>
            <div className="mb-4">
                <Input
                    type="text"
                    name="search"
                    placeholder={PLACEHOLDER_SEARCH}
                    value={searchTerm}
                    onChange={(value) => setSearchTerm(value)}
                    className="w-full"
                />
            </div>
            <div className="w-full bg-[#00000008] rounded-md px-2 py-4">
                {filteredMembers.length > 0 && (
                    <div className="mb-2 pb-2">
                        <Checkbox
                            name="selectAll"
                            value={isAllSelected}
                            onChange={() => {
                                if (isAllSelected) {
                                    handleDeselectAll();
                                } else {
                                    handleSelectAll();
                                }
                            }}
                            options={[{ value: true, label: SELECT_ALL }]}
                        />
                    </div>
                )}

                {filteredMembers.length > 0 ? (
                    <Checkbox
                        multiple={true}
                        options={filteredMembers}
                        value={checkboxValue}
                        onChange={handleChange}
                        containerClassName="max-h-[250px] overflow-y-auto"
                    />
                ) : (
                    <div className="text-center text-gray-500 py-4">
                        {NO_SEARCH_RESULT}
                    </div>
                )}
            </div>
        </BaseControl>
    );
}


export default EmployeeListControl;

type Props = BaseControlProps & {
    value?: Array<number | string>;
    onChange: (value: Props["value"]) => void;
};

const i18nKeys = [
    'BSM.UserOrganisation.Employees.SearchPlaceholder',
    'BSM.UserOrganisation.Employees.NoSearchResult',
    'std_select_all'
]
